import { i18n } from '../i18n.js';
/**
 * Consecutive-timeout feedback.
 *
 * The player this warning is ABOUT was, by definition, not looking when their
 * turn expired — so a three-second toast is the one place it must not live
 * alone. Every strike is written into the storyboard, where it is still there
 * when they come back and find out the next timeout costs them the match.
 *
 * Diff-driven off the authoritative player view, exactly like DeckoutFeedback:
 * the server owns the count, this only renders it.
 */

import { StoryFeed } from './story-feed.js';
let TurnTimeoutFeedback = {};

TurnTimeoutFeedback = (function () {
    'use strict';

    // Mirrors MAX_CONSECUTIVE_TURN_TIMEOUTS in game/managers/turnTimeoutStrikes.js.
    const FORFEIT_AT = 2;

    const messagesByTurn = new Map();

    function translate(key, fallback) {
        const translated = i18n?.t?.(key);
        return translated && translated !== key ? translated : fallback;
    }

    function turnKey(matchId, turn) {
        if (typeof matchId !== 'string' || !matchId.trim()) return null;
        if (!Number.isInteger(turn) || turn < 0) return null;
        return `turnTimeout:${matchId.trim()}:${turn}`;
    }

    function sameMatch(first, second) {
        return Boolean(
            first?.roomId
            && first?.matchId
            && first.roomId === second?.roomId
            && first.matchId === second?.matchId
        );
    }

    function strikes(view, side) {
        const value = Number(view?.[side]?.turnTimeoutStrikes);
        return Number.isInteger(value) && value > 0 ? value : 0;
    }

    /** The one seat whose count went UP in this broadcast, if any. */
    function increasedStrike(previousView, nextView) {
        for (const side of ['player', 'opponent']) {
            const next = strikes(nextView, side);
            if (next > strikes(previousView, side) && next <= FORFEIT_AT) {
                return { side, strikes: next };
            }
        }
        return null;
    }

    function message(side, count) {
        const mine = side === 'player';
        if (count >= FORFEIT_AT) {
            return mine
                ? translate(
                    'game.turnTimeoutFinalPlayer',
                    'Your time ran out twice in a row — you forfeit the match.',
                )
                : translate(
                    'game.turnTimeoutFinalOpponent',
                    'Your opponent ran out of time twice in a row — they forfeit the match.',
                );
        }
        return mine
            ? translate(
                'game.turnTimeoutWarningPlayer',
                'Your time ran out — one more timed-out turn and you forfeit.',
            )
            : translate(
                'game.turnTimeoutWarningOpponent',
                'Your opponent ran out of time — one more and they forfeit.',
            );
    }

    function handleStateUpdate(previousView, nextView) {
        if (!sameMatch(previousView, nextView)) return false;
        const increment = increasedStrike(previousView, nextView);
        if (!increment) return false;

        const key = turnKey(nextView.matchId, nextView.currentTurn);
        if (!key) return false;
        const text = message(increment.side, increment.strikes);
        if (messagesByTurn.get(key) === text) return true;
        if (typeof StoryFeed?.upsertSystemEntry !== 'function') return false;

        StoryFeed.upsertSystemEntry(text, {
            key,
            kind: 'timeout',
            turn: nextView.currentTurn,
            announce: true,
        });
        messagesByTurn.set(key, text);
        return true;
    }

    function reset() {
        messagesByTurn.clear();
    }

    return { handleStateUpdate, reset };
})();

export { TurnTimeoutFeedback };
